import { DateTime } from 'luxon'

import type {
  BlockCondition,
  Blocker,
  BlockResult,
  TimeBlocker
} from '@/types/blocker'
import type { ResourceRef } from '@/types/resource'

/** Responde se o participante satisfaz a condição de um recurso. */
export type GetProgress = (resource: ResourceRef, condition: string) => boolean

const UNLOCKED: BlockResult = { unlocked: true, unmet: [], firstUnmet: undefined }

/**
 * Avalia uma condição de bloqueio contra o progresso do participante.
 *
 * Módulo puro extraído de `useBlocker` para que a derivação client-side possa
 * decidir lock/unlock sem montar hook — e para que o teste rode em `node`.
 * A semântica é a mesma do hook: errar aqui mostra ou esconde um cadeado, não
 * concede progresso; a conclusão continua validada no backend.
 */
export const checkBlockCondition = (
  block: BlockCondition,
  getProgress: GetProgress
): BlockResult => {
  if (!block) return UNLOCKED

  if (block.type === 'and') {
    const results = block.conditions.map(cond =>
      checkBlockCondition(cond, getProgress)
    )
    const allMet = results.every(r => r.unlocked)
    const unmet = results.flatMap(r => r.unmet)
    return { unlocked: allMet, unmet, firstUnmet: unmet[0] }
  }

  if (block.type === 'or') {
    const results = block.conditions.map(cond =>
      checkBlockCondition(cond, getProgress)
    )
    const anyMet = results.some(r => r.unlocked)
    const unmet = results.flatMap(r => r.unmet)
    return {
      unlocked: anyMet,
      unmet: anyMet ? [] : unmet,
      firstUnmet: anyMet ? undefined : unmet[0]
    }
  }

  if (block.type === 'time') {
    const unlockTime = DateTime.fromISO(block.until)
    const ok = !unlockTime.isValid || DateTime.local() >= unlockTime
    return unmetResult(ok, block)
  }

  if (block.type === 'resource') {
    const ok = getProgress(block.resource, block.condition)
    return unmetResult(ok, block)
  }

  return UNLOCKED
}

const unmetResult = (ok: boolean, block: Blocker | TimeBlocker): BlockResult => ({
  unlocked: ok,
  unmet: ok ? [] : [block],
  firstUnmet: ok ? undefined : block
})
